import { CreateDocumentRepoDTO, DocumentRepo } from '@/utils/types/documentRepo/documentRepo';
import {
  DocumentRepoCommitDTO,
} from '@/utils/types/documentRepo/documentRepoCommit';
import names from '@/store/documentRepo/names';
import helpers from '@/store/helpers';
import api from '@/config/api';
import Vue from 'vue';
import {
  DocumentRepoState,
  RootState,
  DocIdCommitId,
  PrevCurrNextCommitId,
  DocumentRepoChartTab,
} from '@/utils/types/store';
import { ActionContext, Module } from 'vuex';

type Ctx = ActionContext<DocumentRepoState, RootState>;

const initialState: DocumentRepoState = {
  // All document repos
  documentRepos: Array<DocumentRepo>(),
  // Currently viewed document repo
  documentRepo: {} as DocumentRepo,
  // Revisions of currently viewed document repo
  documentRepoCommits: Array<DocumentRepoCommitDTO>(),
  // Currently viewed document revision
  documentRepoCommit: {} as DocumentRepoCommitDTO,
  // Currently selected tab
  selectedChartTab: 0,
  // Whether a polling interval is set
  pollingIntervalId: undefined,
};

const documentRepoModule: Module<DocumentRepoState, RootState> = {
  namespaced: true,
  state: initialState,
  getters: {
    currentCommit: (state: DocumentRepoState): DocumentRepoCommitDTO | null => {
      if (!state.documentRepoCommit.id) {
        return null;
      }
      return state.documentRepoCommit;
    },
    previousCommit: (state: DocumentRepoState, getters): DocumentRepoCommitDTO | null => {
      if (!getters.currentCommitId) {
        return null;
      }
      const idx = state.documentRepoCommits.findIndex(
        (c) => c.id === getters.currentCommitId,
      );
      if (idx === 0) return null;
      return state.documentRepoCommits[idx - 1];
    },
    nextCommit: (state: DocumentRepoState, getters): DocumentRepoCommitDTO | null => {
      if (!getters.currentCommitId) {
        return null;
      }
      const idx = state.documentRepoCommits.findIndex(
        (c) => c.id === getters.currentCommitId,
      );
      if (idx === state.documentRepoCommits.length - 1) return null;
      return state.documentRepoCommits[idx + 1];
    },
    currentCommitId: (state, getters): string | null => getters.currentCommit?.id,
    previousCommitId: (state, getters): string | null => getters.previousCommit?.id,
    nextCommitId: (state, getters): string | null => getters.nextCommit?.id,
  },
  mutations: {
    // Document repos
    [names.mutations._SAVE_DOCUMENT_REPOS](state, documentRepos) {
      state.documentRepos = documentRepos;
    },
    [names.mutations._SAVE_DOCUMENT_REPO](state, documentRepo) {
      state.documentRepo = documentRepo;
    },
    [names.mutations._SAVE_DOCUMENT_REPO_COMMITS](state, documentRepoCommits) {
      state.documentRepoCommits = documentRepoCommits;
    },
    [names.mutations._SAVE_DOCUMENT_REPO_COMMIT](state, documentRepoCommit) {
      state.documentRepoCommit = documentRepoCommit;
    },
    [names.mutations._SAVE_DOCUMENT_REPO_CHART_TAB](state, chartTab) {
      state.selectedChartTab = chartTab;
    },
  },
  actions: {
    // Document repos
    loadDocumentRepos({ commit, dispatch }: Ctx) {
      return helpers.axios.get(commit)(
        api.routes.documentRepos,
        names.mutations._SAVE_DOCUMENT_REPOS,
      ).then(() => dispatch('polling'));
    },
    polling({ dispatch, state }: Ctx) {
      const isNotFinishedOrErrored = (dr: DocumentRepo) => dr.status !== 'finished' && dr.status !== 'error';
      const shouldPoll = state.documentRepos.some(isNotFinishedOrErrored);
      state.pollingIntervalId = helpers.polling.check(shouldPoll, state, () => dispatch('loadDocumentRepos'));
    },
    loadDocumentRepo({ commit, dispatch }: Ctx, docId: string) {
      return helpers.axios
        .get(commit)(
          `${api.routes.documentRepos}/${docId}`,
          names.mutations._SAVE_DOCUMENT_REPO,
        )
        // load history of documentComponents
        .then(() => dispatch(
          'documentComponents/loadDocumentRepoHistory', docId, { root: true },
        ));
    },
    loadDocumentRepoCommits({ commit }: Ctx, id: string) {
      return helpers.axios.get(commit)(
        `${api.routes.documentRepos}/${id}/revisions`,
        names.mutations._SAVE_DOCUMENT_REPO_COMMITS,
      );
    },
    loadDocumentRepoCommit({ commit }: Ctx, docIdCommitId: DocIdCommitId) {
      const { docId, commitId } = docIdCommitId;
      return helpers.axios.get(commit)(
        `${api.routes.documentRepos}/${docId}/revisions/${commitId}`,
        names.mutations._SAVE_DOCUMENT_REPO_COMMIT,
      );
    },
    loadCommitData({ dispatch, getters, state }: Ctx, docIdCommitId: DocIdCommitId) {
      // Load data for this commit
      dispatch('loadDocumentRepoCommit', docIdCommitId)
        .then(() => {
          const prevCurrNextCommitId: PrevCurrNextCommitId = {
            docId: docIdCommitId.docId,
            prevId: getters.previousCommitId || undefined,
            currId: state.documentRepoCommit.id,
            nextId: getters.nextCommitId || undefined,
          };
          // Load document components for previous, current, and next commit
          dispatch(
            'documentComponents/loadPrevCurrNextCommitComponents',
            prevCurrNextCommitId,
            { root: true },
          );
        });
    },
    postDocumentRepo({ commit, state, dispatch }: Ctx, payload: CreateDocumentRepoDTO) {
      return Vue.axios
        .post(api.routes.documentRepos, payload)
        .then((result) => {
          const newDocumentRepos = [...state.documentRepos, result.data];
          commit(names.mutations._SAVE_DOCUMENT_REPOS, newDocumentRepos);
        }).then(() => dispatch('polling'));
    },
    deleteDocumentRepo({ dispatch }: Ctx, docId: string) {
      return Vue.axios
        .delete(`${api.routes.documentRepos}/${docId}`)
        .then(() => dispatch('loadDocumentRepos'))
        .catch((error) => {
          throw new Error(`API ${error}`);
        });
    },
    selectDocumentRepoChartTab({ commit }: Ctx, chartTab: DocumentRepoChartTab) {
      commit(names.mutations._SAVE_DOCUMENT_REPO_CHART_TAB, chartTab);
    },
    clearDocumentRepo({ commit }: Ctx) {
      commit(names.mutations._SAVE_DOCUMENT_REPO, {});
      commit(names.mutations._SAVE_DOCUMENT_REPO_COMMITS, []);
      commit(names.mutations._SAVE_DOCUMENT_REPO_COMMIT, {});
    },
  },
};

export default documentRepoModule;
